import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import type { ComplianceAction, ComplianceActionType } from "@/types/database";
import { notifyHostComplianceAction } from "@/lib/notifications";

export function useComplianceActions() {
  const { activeOrgId } = useAuth();

  return useQuery({
    queryKey: ["compliance_actions", activeOrgId],
    enabled: !!activeOrgId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("compliance_actions")
        .select(`
          *,
          host:hosts!compliance_actions_host_id_fkey(
            *,
            profile:profiles!hosts_user_id_fkey(*),
            unit:units!hosts_unit_id_fkey(*)
          )
        `)
        .eq("organization_id", activeOrgId!)
        .order("issued_at", { ascending: false });
      if (error) throw error;
      return data as ComplianceAction[];
    },
  });
}

export function useCreateComplianceAction() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user, activeOrgId } = useAuth();

  return useMutation({
    mutationFn: async ({
      hostId,
      actionType,
      description,
    }: {
      hostId: string;
      actionType: ComplianceActionType;
      description: string;
    }) => {
      if (!activeOrgId) throw new Error("No active organization");

      const { data, error } = await supabase
        .from("compliance_actions")
        .insert({
          host_id: hostId,
          organization_id: activeOrgId,
          action_type: actionType,
          description,
          issued_by: user?.fullName ?? "Admin",
          issued_at: new Date().toISOString(),
        })
        .select()
        .single();
      if (error) throw error;

      // Mirror to audit_logs for traceability
      await supabase.from("audit_logs").insert({
        organization_id: activeOrgId,
        action: `Compliance action logged: ${actionType}`,
        entity: `Host ${hostId}`,
        detail: description,
        user_name: user?.fullName ?? "Admin",
      });

      // Notify the host about the compliance action (fire-and-forget)
      // Fetch user_id and unit number from the host record
      const { data: hostRecord } = await supabase
        .from("hosts")
        .select("user_id, unit:units!hosts_unit_id_fkey(unit_number)")
        .eq("id", hostId)
        .single();
      if (hostRecord?.user_id) {
        const unitNumber = (hostRecord.unit as any)?.unit_number ?? "—";
        notifyHostComplianceAction(hostRecord.user_id, activeOrgId, actionType, description, unitNumber)
          .catch((e) => console.warn("[notify] Compliance action notification failed:", e));
      }

      return data as ComplianceAction;
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ["compliance_actions", activeOrgId] });
      queryClient.invalidateQueries({ queryKey: ["audit_logs", activeOrgId] });
      const labels: Record<ComplianceActionType, string> = {
        notice: "Notice issued",
        warning: "Warning issued",
        penalty: "Penalty recorded",
        order: "Compliance order recorded",
        reminder: "Renewal reminder logged",
      };
      toast({
        title: labels[vars.actionType] ?? "Action logged",
        description: "Compliance action has been recorded and added to the audit log.",
      });
    },
    onError: (error: Error) => {
      toast({ title: "Error logging action", description: error.message, variant: "destructive" });
    },
  });
}
